import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaSync, FaTags, FaSearch } from 'react-icons/fa';
import inventoryService from '../services/inventoryService';
import '../styles/InventoryManagement.css';

const CategoriaInventarioManagement = () => {
    const [categorias, setCategorias] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [currentCategoria, setCurrentCategoria] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [formData, setFormData] = useState({
        CATEGORIAINVENTARIO_NOMBRE: '',
        CATEGORIAINVENTARIO_DESCRIPCION: ''
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        loadCategorias(currentPage, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize]);

    const loadCategorias = async (page = 1, ps = 10) => {
        try {
            setLoading(true);
            setError('');
            const response = await inventoryService.categories.getCategories(page, ps, searchTerm);
            if (response.success) {
                const payload = response.data;
                if (Array.isArray(payload)) {
                    // legacy: server returned full array
                    const all = (payload || []).filter(c => c.CATEGORIAINVENTARIO_ID !== 'CAT-000');
                    const tot = all.length;
                    if (ps && ps > 0) {
                        const start = (page - 1) * ps;
                        const pageRows = all.slice(start, start + ps);
                        setCategorias(pageRows);
                        setTotal(tot);
                        setTotalPages(Math.max(1, Math.ceil(tot / ps)));
                        setCurrentPage(page);
                        setPageSize(ps);
                    } else {
                        setCategorias(all);
                        setTotal(tot);
                        setTotalPages(1);
                        setCurrentPage(1);
                        setPageSize(ps);
                    }
                } else if (payload && payload.data) {
                    const data = (payload.data || []).filter(c => c.CATEGORIAINVENTARIO_ID !== 'CAT-000');
                    const tot = payload.total || 0;
                    const computedPages = payload.totalPages || Math.ceil(tot / ps);
                    setCategorias(data);
                    setTotal(tot);
                    setTotalPages(Math.max(1, computedPages));
                    setCurrentPage(payload.page || page);
                    setPageSize(payload.pageSize || ps);
                } else {
                    setCategorias([]);
                    setTotal(0);
                    setTotalPages(1);
                }
            } else {
                setError(response.message || 'Error al cargar las categorías');
            }
        } catch (error) {
            setError(error.message || 'Error de conexión');
        } finally {
            setLoading(false);
        }
    };

    // Server-side pagination: categorias already contains current page rows
    const paginatedCategorias = categorias;

    // Resetear página cuando cambie la búsqueda (debounced reload)
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    useEffect(() => {
        const t = setTimeout(() => {
            setCurrentPage(1);
            loadCategorias(1, pageSize);
        }, 350);
        return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.CATEGORIAINVENTARIO_NOMBRE?.trim()) {
            newErrors.CATEGORIAINVENTARIO_NOMBRE = 'El nombre de la categoría es obligatorio';
        } else if (formData.CATEGORIAINVENTARIO_NOMBRE.trim().length < 2) {
            newErrors.CATEGORIAINVENTARIO_NOMBRE = 'El nombre debe tener al menos 2 caracteres';
        } else if (formData.CATEGORIAINVENTARIO_NOMBRE.trim().length > 24) {
            newErrors.CATEGORIAINVENTARIO_NOMBRE = 'El nombre no puede exceder 24 caracteres';
        }

        if (!formData.CATEGORIAINVENTARIO_DESCRIPCION?.trim()) {
            newErrors.CATEGORIAINVENTARIO_DESCRIPCION = 'La descripción es obligatoria';
        } else if (formData.CATEGORIAINVENTARIO_DESCRIPCION.trim().length > 24) {
            newErrors.CATEGORIAINVENTARIO_DESCRIPCION = 'La descripción no puede exceder 24 caracteres';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const resetForm = () => {
        setFormData({ CATEGORIAINVENTARIO_NOMBRE: '', CATEGORIAINVENTARIO_DESCRIPCION: '' });
        setErrors({});
        setCurrentCategoria(null);
    };

    const handleCloseModal = () => {
        setShowForm(false);
        resetForm();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);
            const categoriaData = {
                CATEGORIAINVENTARIO_NOMBRE: formData.CATEGORIAINVENTARIO_NOMBRE.trim(),
                CATEGORIAINVENTARIO_DESCRIPCION: formData.CATEGORIAINVENTARIO_DESCRIPCION.trim()
            };

            let response;
            if (currentCategoria) {
                console.log('Actualizando categoría:', currentCategoria.CATEGORIAINVENTARIO_ID, categoriaData);
                response = await inventoryService.categories.updateCategory(currentCategoria.CATEGORIAINVENTARIO_ID, categoriaData);
            } else {
                console.log('Creando nueva categoría:', categoriaData);
                response = await inventoryService.categories.createCategory(categoriaData);
            }

            console.log('Respuesta del servidor:', response);
            
            if (response.success) {
                await loadCategorias();
                handleCloseModal();
                setError('');
            } else {
                setError(response.message || 'Error al procesar la categoría');
            }
        } catch (error) {
            setError(error.message || 'Error de conexión');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (categoria) => {
        setCurrentCategoria(categoria);
        setFormData({
            CATEGORIAINVENTARIO_NOMBRE: categoria.CATEGORIAINVENTARIO_NOMBRE,
            CATEGORIAINVENTARIO_DESCRIPCION: categoria.CATEGORIAINVENTARIO_DESCRIPCION || ''
        });
        setShowForm(true);
        setErrors({});
    };

    const handleDelete = async (id) => {
        try {
            setLoading(true);
            const response = await inventoryService.categories.deleteCategory(id);
            
            if (response.success) {
                await loadCategorias();
                setError('');
            } else {
                setError(response.message || 'Error al eliminar la categoría');
            }
        } catch (error) {
            setError(error.message || 'Error de conexión');
        } finally {
            setLoading(false);
        }
    };

    if (error) {
        return (
            <div className="categoria-inventario-management-container">
                <div className="categoria-inventario-management-header">
                    <h1>Gestión de Categorías</h1>
                    <p style={{ color: 'red' }}>Error: {error}</p>
                </div>
                <button onClick={loadCategorias}>Reintentar</button>
            </div>
        );
    }

    return (
        <div className="inventory-management-container">
            <div className="inventory-management-header">
                <h1>Gestión de Categorías</h1>
                <p>Administre las categorías del inventario médico de FEPAST</p>
            </div>
            <div className="inventory-management-actions">
                <div className="search-section">
                    <FaSearch className="search-icon" />
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Buscar categorías por nombre, descripción o ID..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                </div>

                <div className="action-buttons">
                    <button className="register-btn" onClick={() => window.location.href = '/inventory'}>
                        <FaTags /> Ver Inventario
                    </button>
                    <button className="register-btn" onClick={loadCategorias} disabled={loading}>
                        <FaSync /> {loading ? 'Cargando...' : 'Actualizar'}
                    </button>
                    <button className="register-btn" onClick={() => setShowForm(true)}>
                        <FaPlus /> Nueva Categoría
                    </button>
                </div>
            </div>
            <div className="inventory-table-container">
                <table className="inventory-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Categoría</th>
                            <th>Descripción</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedCategorias.length > 0 ? (
                            paginatedCategorias.map((categoria) => (
                                <tr key={categoria.CATEGORIAINVENTARIO_ID}>
                                    <td><span className="product-id">{categoria.CATEGORIAINVENTARIO_ID}</span></td>
                                    <td>
                                        <div className="product-name">
                                            <div className="product-icon">
                                                <FaTags />
                                            </div>
                                            {categoria.CATEGORIAINVENTARIO_NOMBRE}
                                        </div>
                                    </td>
                                    <td>{categoria.CATEGORIAINVENTARIO_DESCRIPCION}</td>
                                    <td>
                                        <div className="actions">
                                            <button
                                                className="action-btn edit-btn"
                                                onClick={() => {
                                                    if (categoria.CATEGORIAINVENTARIO_ID === 'CAT-000') {
                                                        alert('No se puede editar la categoría por defecto');
                                                        return;
                                                    }
                                                    handleEdit(categoria);
                                                }}
                                                title="Editar categoría"
                                            >
                                                <FaEdit />
                                            </button>
                                            <button
                                                className="action-btn delete-btn"
                                                onClick={() => {
                                                    if (categoria.CATEGORIAINVENTARIO_ID === 'CAT-000') {
                                                        alert('No se puede eliminar la categoría por defecto');
                                                        return;
                                                    }
                                                    if (window.confirm('¿Está seguro de que desea eliminar esta categoría?')) {
                                                        handleDelete(categoria.CATEGORIAINVENTARIO_ID);
                                                    }
                                                }}
                                                title="Eliminar categoría"
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                                    No hay categorías registradas
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="records-info" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                <div>
                    Mostrando <strong>{categorias.length}</strong> de <strong>{total}</strong> categorías
                    {searchTerm && ` (filtradas)`}
                </div>
                <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button className="btn-secondary" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Anterior</button>
                    <span> Página <strong>{currentPage}</strong> de <strong>{totalPages}</strong> </span>
                    <button className="btn-secondary" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Siguiente</button>
                    <label style={{ marginLeft: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>Por página:</span>
                        <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                        </select>
                    </label>
                </div>
            </div>
            
            {showForm && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>
                                <FaTags />
                                {currentCategoria ? 'Editar Categoría' : 'Nueva Categoría'}
                            </h3>
                            <button className="modal-close" onClick={handleCloseModal}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={handleSubmit} className="inventory-form">
                                <div className="form-section">
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="CATEGORIAINVENTARIO_NOMBRE">Nombre de la Categoría</label>
                                            <input
                                                type="text"
                                                id="CATEGORIAINVENTARIO_NOMBRE"
                                                name="CATEGORIAINVENTARIO_NOMBRE"
                                                value={formData.CATEGORIAINVENTARIO_NOMBRE}
                                                onChange={handleInputChange}
                                                placeholder="Ej: Medicamentos, Instrumentos..."
                                                className={errors.CATEGORIAINVENTARIO_NOMBRE ? 'error' : ''}
                                                maxLength={24}
                                            />
                                            {errors.CATEGORIAINVENTARIO_NOMBRE && <span className="error-message">{errors.CATEGORIAINVENTARIO_NOMBRE}</span>}
                                        </div>
                                        
                                        <div className="form-group">
                                            <label htmlFor="CATEGORIAINVENTARIO_DESCRIPCION">Descripción</label>
                                            <input
                                                type="text"
                                                id="CATEGORIAINVENTARIO_DESCRIPCION"
                                                name="CATEGORIAINVENTARIO_DESCRIPCION"
                                                value={formData.CATEGORIAINVENTARIO_DESCRIPCION}
                                                onChange={handleInputChange}
                                                placeholder="Descripción de la categoría"
                                                className={errors.CATEGORIAINVENTARIO_DESCRIPCION ? 'error' : ''}
                                                maxLength={24}
                                            />
                                            {errors.CATEGORIAINVENTARIO_DESCRIPCION && <span className="error-message">{errors.CATEGORIAINVENTARIO_DESCRIPCION}</span>}
                                        </div>
                                    </div>
                                </div>

                                <div className="form-actions">
                                    <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                                        Cancelar
                                    </button>
                                    <button type="submit" className="btn-primary" disabled={loading}>
                                        {loading ? 'Procesando...' : (currentCategoria ? 'Actualizar' : 'Crear')} Categoría
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CategoriaInventarioManagement;